import { useAction, useAtom } from '@reatom/npm-react'
import React, { useState } from 'react'
import styled from 'styled-components'

import { Copied1Icon } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import {
  useWalletAssetBalance,
  useWalletAssetBalanceUsd,
} from '@root/entities/wallet/hooks'
import { setTradingWalletAction } from '@root/features/trade/trading'
import { toggleFavoriteWalletAction } from '@root/features/wallet'
import { useClipboard } from '@root/shared'
import { AmountFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'
import {
  CopyIcon,
  RadioButton,
  Separator as Separator_,
  Star,
  Truncate,
} from '@root/shared/ui-kit'

import type { Pair } from '@root/entities/pair'
import type { WalletModel } from '@root/entities/wallet/atoms'

type Props = {
  pair: Pair.PairMinimal
  wallet: WalletModel
  selected: boolean
}

export const InstantTradeWallet: React.FC<Props> = ({
  wallet,
  pair,
  selected,
}) => {
  const { t } = useTranslation()
  const toggleFavorite = useAction(toggleFavoriteWalletAction)
  const setTradingWallet = useAction(setTradingWalletAction)
  const { copyToClipboard } = useClipboard()

  const baseAssetBalance = useWalletAssetBalance({
    currency: pair.base,
    wallet,
  })

  const baseAssetBalanceUsd = useWalletAssetBalanceUsd({
    currency: pair.base,
    wallet,
  })

  const quoteAssetBalance = useWalletAssetBalance({
    currency: pair.quote,
    wallet,
  })
  const quoteAssetBalanceUsd = useWalletAssetBalanceUsd({
    currency: pair.quote,
    wallet,
  })
  const [name] = useAtom(wallet.name)
  const [favorite] = useAtom(wallet.favorite)

  const [isCopied, setIsCopied] = useState(false)

  const handleCopyToClipboard = async () => {
    await copyToClipboard(wallet.address, {
      snackbarText: t('wallet.address_copied_snackbar'),
    })
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 1000)
  }
  const handleFavoriteClick: React.MouseEventHandler = (e) => {
    e.stopPropagation()
    toggleFavorite(wallet.id)
  }

  const Icon = isCopied ? Copied1Icon : CopyIcon

  return (
    <Root onClick={() => setTradingWallet(wallet.id)}>
      <Favorite onClick={handleFavoriteClick} filled={favorite} size={16} />
      <WalletInfo>
        <Name>{name}</Name>
        <Address>
          <Truncate start={4} end={5}>
            {wallet.address}
          </Truncate>
          <IconCopy onClick={handleCopyToClipboard}>
            <Icon size={12} />
          </IconCopy>
        </Address>
      </WalletInfo>
      <Action>
        <Asset>
          <Base>
            <Amount>
              <AmountFormatted amount={baseAssetBalance} roundingMode="trunc" />
              <CurrencyIcon
                currency={pair.base}
                size={10}
                withGoogleLens={false}
                withPreview={false}
              />
            </Amount>
            <Value>
              <AmountFormatted
                amount={baseAssetBalanceUsd}
                currency="$"
                roundingMode="trunc"
              />
            </Value>
          </Base>
          <Separator vertical />
          <Quote>
            <Amount>
              <AmountFormatted
                amount={quoteAssetBalance}
                roundingMode="trunc"
              />
              <CurrencyIcon
                currency={pair.quote}
                size={10}
                withGoogleLens={false}
              />
            </Amount>
            <Value>
              <AmountFormatted
                amount={quoteAssetBalanceUsd}
                currency="$"
                roundingMode="trunc"
              />
            </Value>
          </Quote>
        </Asset>
        <RadioButton checked={selected} />
      </Action>
    </Root>
  )
}

const Root = styled.div`
  border: 1px solid
    var(--instant-trade-wallet-border-color, var(--backgroundA-3));

  cursor: pointer;

  height: 42px;
  border-radius: 8px;
  gap: 4px;
  padding-inline-start: 6px;

  display: grid;
  grid-template-columns: auto 80px 1fr;
  justify-content: center;
  align-items: center;

  transition: border 0.2s ease-in-out;

  &:hover {
    border: 1px solid var(--primary-9);
  }
`

const Action = styled.span`
  display: grid;
  grid-template-columns: 1fr auto;

  align-items: center;
  height: 100%;

  padding-inline-end: 6px;
`

const IconCopy = styled.span``

const Favorite = styled(Star)``

const WalletInfo = styled.div`
  display: grid;

  padding-inline-end: 5px;
  gap: 2px;
`

const Name = styled.span`
  color: var(--text-12);
  text-overflow: ellipsis;

  font-size: 10px;
  font-weight: 600;

  overflow: hidden;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Address = styled.span`
  color: var(--text-9);

  font-size: 10px;
  font-weight: 500;
  gap: 4px;

  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
`

const Asset = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;

  border-radius: 6px;
  background: var(--appearanceA-2);

  gap: 8px;
  padding: 4px 10px;
  margin-inline: 4px;
`

const Separator = styled(Separator_)`
  background: var(--backgroundA-3);
`

const Base = styled.span`
  color: var(--text-12);

  display: grid;
  align-content: center;
`

const Amount = styled.span`
  color: var(--text-12);

  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  justify-items: start;
  gap: 2px;

  font-size: 10px;
  line-height: 16px;
  font-weight: 600;
`

const Value = styled.span`
  color: var(--text-9);

  font-size: 9px;
  font-weight: 500;
`

const Quote = styled.span`
  color: var(--text-12);

  display: grid;
  align-content: center;
  justify-content: start;
`
